import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import customerOrderService from '../services/customerOrderService';
import { usePermissions } from './PermissionContext';

// ── Sound ─────────────────────────────────────────────────────────────────────
export function playOrderDing() {
  try {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    const play = (freq, start, dur, vol = 0.55) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = 'sine';
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0, start);
      gain.gain.linearRampToValueAtTime(vol, start + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.001, start + dur);
      osc.start(start);
      osc.stop(start + dur);
    };
    ctx.resume().then(() => {
      const t = ctx.currentTime;
      play(880,  t,        0.30);
      play(1100, t + 0.18, 0.30);
      play(1320, t + 0.36, 0.40);
    });
  } catch {
    // silent fail — browser may block autoplay
  }
}

// ── Context ───────────────────────────────────────────────────────────────────
const OrderNotificationContext = createContext({
  pendingCount: 0,
  unseenCount: 0,
  newOrdersSignal: 0,
  newConfirmedSignal: 0,
  markSeen: () => {},
});

export function useOrderNotifications() {
  return useContext(OrderNotificationContext);
}

const ADMIN_ORDER_PAGE   = '/admin/orders';
const FACTORY_ORDER_PAGE = '/factory/orders';
const POLL_INTERVAL      = 20000; // 20 s
const BEEP_INTERVAL      = 8000;  // repeat beep every 8 s until acknowledged

export function OrderNotificationProvider({ children }) {
  const { hasModule } = usePermissions();
  const location = useLocation();
  const navigate = useNavigate();

  // The employee/driver portal shares this browser's localStorage with the admin app, so a
  // still-technically-valid admin token can linger while we're on /portal — polling admin order
  // endpoints there serves no purpose (nothing here renders in the portal) and just risks noisy
  // 401s if that token has since been invalidated. Gate on route, not just module access.
  const onPortal   = location.pathname.startsWith('/portal');
  const canAdmin   = !onPortal && hasModule('admin.orders');
  const canFactory = !onPortal && hasModule('production.orders');

  // ── Admin track: new portal orders ────────────────────────────────────────
  const [pendingCount,    setPendingCount]    = useState(0);
  const [unseenCount,     setUnseenCount]     = useState(0);
  const [newOrdersSignal, setNewOrdersSignal] = useState(0);
  const adminKnownIdsRef = useRef(null);
  const onAdminPageRef   = useRef(false);

  // ── Factory track: newly-confirmed orders ─────────────────────────────────
  const [newConfirmedSignal, setNewConfirmedSignal] = useState(0);
  const factoryKnownIdsRef = useRef(null);
  const onFactoryPageRef   = useRef(false);

  // ── Factory persistent alert (unacknowledged new orders) ──────────────────
  // { orders: [{id, customerName}], count: N }
  const [factoryAlert, setFactoryAlert] = useState(null);
  const beepIntervalRef = useRef(null);

  const stopBeeping = useCallback(() => {
    if (beepIntervalRef.current) {
      clearInterval(beepIntervalRef.current);
      beepIntervalRef.current = null;
    }
  }, []);

  const startBeeping = useCallback(() => {
    stopBeeping();
    playOrderDing();
    beepIntervalRef.current = setInterval(playOrderDing, BEEP_INTERVAL);
  }, [stopBeeping]);

  const acknowledgeFactory = useCallback(() => {
    stopBeeping();
    setFactoryAlert(null);
  }, [stopBeeping]);

  // ── Admin toast (kept auto-dismiss — admin just needs a heads-up) ─────────
  const [toast, setToast] = useState(null);
  const toastTimerRef = useRef(null);

  const showToast = useCallback((msg, sub, variant = 'success') => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToast({ msg, sub, variant });
    toastTimerRef.current = setTimeout(() => setToast(null), 10000);
  }, []);

  // ── Page tracking ─────────────────────────────────────────────────────────
  useEffect(() => {
    const onAdmin   = location.pathname.startsWith(ADMIN_ORDER_PAGE);
    const onFactory = location.pathname.startsWith(FACTORY_ORDER_PAGE);
    onAdminPageRef.current   = onAdmin;
    onFactoryPageRef.current = onFactory;
    if (onAdmin || onFactory) {
      setUnseenCount(0);
      setToast(null);
    }
    // Auto-acknowledge when factory user navigates to the orders page
    if (onFactory) {
      acknowledgeFactory();
    }
  }, [location.pathname, acknowledgeFactory]);

  // ── Admin poll: pending portal orders ─────────────────────────────────────
  const pollAdmin = useCallback(async () => {
    if (!canAdmin) return;
    try {
      const orders = await customerOrderService.getOrders({ status: 'pending', submittedVia: 'portal' });
      const currentIds = new Set(orders.map(o => o.id));
      setPendingCount(orders.length);

      if (adminKnownIdsRef.current === null) {
        adminKnownIdsRef.current = currentIds;
        return;
      }

      const newOrders = orders.filter(o => !adminKnownIdsRef.current.has(o.id));
      adminKnownIdsRef.current = currentIds;
      if (newOrders.length === 0) return;

      playOrderDing();
      setNewOrdersSignal(s => s + 1);

      if (!onAdminPageRef.current) {
        const names = [...new Set(newOrders.map(o => o.Customer?.name).filter(Boolean))];
        setUnseenCount(c => c + newOrders.length);
        const msg = newOrders.length === 1 ? 'New order received' : `${newOrders.length} new orders received`;
        showToast(msg, names.join(', '), 'success');
      }
    } catch { /* skip */ }
  }, [canAdmin, showToast]);

  // ── Factory poll: newly-confirmed orders ──────────────────────────────────
  const pollFactory = useCallback(async () => {
    if (!canFactory) return;
    try {
      const orders = await customerOrderService.getOrders({ status: 'confirmed,ready' });
      const currentIds = new Set(orders.map(o => o.id));

      if (factoryKnownIdsRef.current === null) {
        factoryKnownIdsRef.current = currentIds;
        return;
      }

      const newOrders = orders.filter(o => !factoryKnownIdsRef.current.has(o.id));
      factoryKnownIdsRef.current = currentIds;
      if (newOrders.length === 0) return;

      setNewConfirmedSignal(s => s + 1);

      // Badge the navbar Orders icon, same as the admin track does for pending orders
      if (!onFactoryPageRef.current) {
        setUnseenCount(c => c + newOrders.length);
      }

      // Only beep for factory users — admins already approved the order and don't need the alert
      if (!onFactoryPageRef.current && !canAdmin) {
        // Accumulate unacknowledged orders and start persistent beeping
        const incoming = newOrders.map(o => ({
          id: o.id,
          customerName: o.Customer?.name || `Order #${o.id}`,
        }));
        setFactoryAlert(prev => {
          const existing = prev?.orders || [];
          const existingIds = new Set(existing.map(o => o.id));
          const merged = [...existing, ...incoming.filter(o => !existingIds.has(o.id))];
          return { orders: merged, count: merged.length };
        });
        startBeeping();
      }
    } catch { /* skip */ }
  }, [canFactory, canAdmin, startBeeping]);

  // ── Intervals ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!canAdmin) return;
    pollAdmin();
    const id = setInterval(pollAdmin, POLL_INTERVAL);
    return () => clearInterval(id);
  }, [canAdmin, pollAdmin]);

  useEffect(() => {
    if (!canFactory) return;
    pollFactory();
    const id = setInterval(pollFactory, POLL_INTERVAL);
    return () => clearInterval(id);
  }, [canFactory, pollFactory]);

  // ── Re-poll on focus / visibility ─────────────────────────────────────────
  useEffect(() => {
    const onFocus   = () => { pollAdmin(); pollFactory(); };
    const onVisible = () => { if (document.visibilityState === 'visible') { pollAdmin(); pollFactory(); } };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [pollAdmin, pollFactory]);

  // Cleanup beep interval on unmount
  useEffect(() => () => stopBeeping(), [stopBeeping]);

  const markSeen = useCallback(() => {
    setUnseenCount(0);
    setToast(null);
  }, []);

  // Cleanup admin toast timer
  useEffect(() => () => { if (toastTimerRef.current) clearTimeout(toastTimerRef.current); }, []);

  const VARIANT_CLASSES = { success: 'alert-success', warning: 'alert-warning' };

  return (
    <OrderNotificationContext.Provider
      value={{ pendingCount, unseenCount, newOrdersSignal, newConfirmedSignal, markSeen }}
    >
      {children}

      {/* ── Factory persistent alert — beeps until acknowledged ─────────── */}
      {factoryAlert && (
        <div style={{
          position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 9999,
          background: '#fff3cd', borderTop: '3px solid #ffc107',
          padding: '14px 20px', boxShadow: '0 -4px 16px rgba(0,0,0,0.15)',
          display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap',
        }}>
          <span style={{ fontSize: '1.6rem', lineHeight: 1, animation: 'factoryBell 1s infinite' }}>🔔</span>
          <div style={{ flex: 1, minWidth: 200 }}>
            <strong style={{ fontSize: 15 }}>
              {factoryAlert.count === 1 ? 'New order ready for production!' : `${factoryAlert.count} new orders ready for production!`}
            </strong>
            <div className="text-muted" style={{ fontSize: 13, marginTop: 2 }}>
              {[...new Set(factoryAlert.orders.map(o => o.customerName))].join(', ')}
            </div>
          </div>
          <button
            className="btn btn-warning btn-sm fw-semibold"
            onClick={() => { acknowledgeFactory(); navigate(FACTORY_ORDER_PAGE); }}
          >
            View Orders →
          </button>
          <button
            className="btn btn-outline-secondary btn-sm"
            onClick={acknowledgeFactory}
            title="Acknowledge — stop the alert"
          >
            Acknowledge ✓
          </button>
        </div>
      )}

      {/* Bell pulse animation */}
      <style>{`
        @keyframes factoryBell {
          0%, 100% { transform: rotate(0deg); }
          20% { transform: rotate(-15deg); }
          40% { transform: rotate(15deg); }
          60% { transform: rotate(-10deg); }
          80% { transform: rotate(10deg); }
        }
      `}</style>

      {/* ── Admin auto-dismiss toast ─────────────────────────────────────── */}
      {toast && (
        <div style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 9998, minWidth: 280, maxWidth: 360 }}>
          <div className={`alert ${VARIANT_CLASSES[toast.variant] || 'alert-success'} shadow d-flex align-items-start gap-2 mb-0`} role="alert">
            <span style={{ fontSize: '1.3rem', lineHeight: 1 }}>🔔</span>
            <div className="flex-grow-1">
              <strong>{toast.msg}</strong>
              {toast.sub && <div className="small text-muted mt-1">{toast.sub}</div>}
            </div>
            <button type="button" className="btn-close btn-sm" onClick={() => setToast(null)} aria-label="Close" />
          </div>
        </div>
      )}
    </OrderNotificationContext.Provider>
  );
}
